/**
 * BriefingAssistantOverlay - AI-assisted briefing editor
 *
 * Embedded component with chat (left 1/3) and editor (right 2/3).
 * Supports three implicit modes: IDLE, EDIT, CHAT.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import SimpleMDE from 'react-simplemde-editor';
import TextareaAutosize from 'react-textarea-autosize';
import { toast } from 'react-toastify';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';

import { SparkProjectDetails, SparkFlow } from '../../types/sparkTypes';
import { useAtlasStore } from '../../../../store/atlasStore';
import { useBriefingSSE } from '../../hooks/useBriefingSSE';
import { useCitation } from '../../../../hooks/useCitation';
import { normalizeMarkdownBullets } from '../../utils/markdown';
import { useTypewriter } from '../../../../hooks/useTypewriter';
import { ThinkingPanel } from './ThinkingPanel';
import { ConfirmationModal } from '../modals/ConfirmationModal';

import styles from './BriefingAssistantOverlay.module.css';

// Types
interface BriefingPatch {
  search: string;
  replace: string;
}

interface BriefingMessage {
  id: string;
  role: 'user' | 'assistant' | 'error';
  content: string;
  timestamp: string;
  patches?: BriefingPatch[];
  isSynthetic?: boolean;
  thinkingSteps?: import('../../types/briefingTypes').ThinkingStep[];
  /** Whether the typewriter animation has completed */
  isAnimated?: boolean;
}

export interface BriefingAssistantHandle {
  saveAndClose: () => Promise<void>;
  discardChanges: () => void;
  hasChanges: boolean;
  isSaving: boolean;
}

interface BriefingAssistantOverlayProps {
  project: SparkProjectDetails;
  flow: SparkFlow | null;
  onBriefingUpdated?: () => void;
  onClose: () => void;
  initialContent?: string | null;
  /** Callback to notify parent of mode state changes for alert bar */
  onModeChange?: (state: { isEditing: boolean; isChatting: boolean; isStarting: boolean; isThinking: boolean }) => void;
}

// Helper to normalize whitespace for comparison
const normalize = (s: string) => (s || '').replace(/\r\n/g, '\n').trim();

// Generate unique message ID
const generateMessageId = () => `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * TypewriterContent - Renders text with typewriter animation
 */
interface TypewriterContentProps {
  content: string;
  isAnimated: boolean;
  onAnimationComplete: () => void;
  onTyping?: () => void;
  markdownComponents: any;
  markdownPlugins: any[];
}

const TypewriterContent: React.FC<TypewriterContentProps> = ({
  content,
  isAnimated,
  onAnimationComplete,
  onTyping,
  markdownComponents,
  markdownPlugins,
}) => {
  const { displayedText, isTyping } = useTypewriter(
    isAnimated ? '' : content,  // Only animate if not already animated
    {
      speed: 200,
      byWord: true,
      onComplete: onAnimationComplete,
    }
  );

  // Notify parent when typing progresses (for auto-scroll)
  useEffect(() => {
    if (isTyping && onTyping) {
      onTyping();
    }
  }, [displayedText, isTyping, onTyping]);

  // If already animated, show full content
  const textToShow = isAnimated ? content : displayedText;

  return (
    <ReactMarkdown
      components={markdownComponents}
      remarkPlugins={markdownPlugins}
    >
      {normalizeMarkdownBullets(textToShow)}
    </ReactMarkdown>
  );
};

export const BriefingAssistantOverlay = forwardRef<BriefingAssistantHandle, BriefingAssistantOverlayProps>(({
  project,
  flow,
  onBriefingUpdated,
  onClose,
  initialContent,
  onModeChange,
}, ref) => {
  // === Restore from sessionStorage (eager, survives StrictMode) ===

  // Read saved session state once via lazy initializer. The key is consumed immediately
  // so StrictMode's double-mount doesn't lose data.
  const [restoredState] = useState<{
    workingBriefing?: string;
    messages?: BriefingMessage[];
    sessionId?: string;
  } | null>(() => {
    const sessionKey = `briefing_assistant_${project.fqn}`;
    const saved = sessionStorage.getItem(sessionKey);
    if (!saved) return null;
    sessionStorage.removeItem(sessionKey);
    try {
      const parsed = JSON.parse(saved);
      // Validate that we have meaningful data to restore
      const hasValidData = parsed.workingBriefing != null || Array.isArray(parsed.messages);
      if (!hasValidData) return null;
      return {
        workingBriefing: parsed.workingBriefing ?? undefined,
        messages: Array.isArray(parsed.messages) ? parsed.messages : undefined,
        sessionId: parsed.sessionId ?? undefined,
      };
    } catch {
      return null;
    }
  });

  // === State ===

  // Briefing content state - use initialContent if provided (e.g., from uploaded DOCX)
  const [workingBriefing, setWorkingBriefing] = useState(
    restoredState?.workingBriefing ?? initialContent ?? project.briefing ?? ''
  );
  const [savedBriefing] = useState(project.briefing || ''); // Original on open
  const [editSnapshot, setEditSnapshot] = useState(''); // Snapshot when entering edit mode

  // Mode state (derived from isEditing and isChatting)
  const [isEditing, setIsEditing] = useState(false);
  const [isChatting, setIsChatting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Chat state
  const [messages, setMessages] = useState<BriefingMessage[]>(restoredState?.messages ?? []);
  const [inputText, setInputText] = useState('');
  const [briefingEditedSinceLastTurn, setBriefingEditedSinceLastTurn] = useState(false);

  // Loading state for initial greeting
  const [isLoadingGreeting, setIsLoadingGreeting] = useState(!restoredState);

  // Session state for SSE
  const [sessionId, setSessionId] = useState<string | undefined>(restoredState?.sessionId);

  // Track which message thinking panels are expanded (by message ID)
  const [expandedThinkingPanels, setExpandedThinkingPanels] = useState<Set<string>>(new Set());
  // Track if live thinking panel (during SSE) is expanded
  const [isLiveThinkingExpanded, setIsLiveThinkingExpanded] = useState(false);
  // Ref to track current expanded state (avoids stale closure in async handlers)
  const isLiveThinkingExpandedRef = useRef(false);

  // Discard confirmation modal state
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

  // Upload banner state - show when content was uploaded
  const [showUploadBanner, setShowUploadBanner] = useState(!!initialContent);

  // Patch animation state
  const [pendingPatches, setPendingPatches] = useState<BriefingPatch[]>([]);
  const [currentPatchIndex, setCurrentPatchIndex] = useState<number>(-1);
  const [isAnimatingPatches, setIsAnimatingPatches] = useState(false);
  const [patchAnimationPhase, setPatchAnimationPhase] = useState<'idle' | 'blur' | 'reveal' | 'collapse'>('idle');
  // Track SOURCE POSITIONS of highlighted ranges (start/end offsets in source markdown)
  // This avoids the bug where text matching highlights wrong sections with same words
  // Static highlights (already animated, no animation)
  const [highlightedRanges, setHighlightedRanges] = useState<Array<{ start: number; end: number }>>([]);
  // New highlight for current animation (will animate)
  const [newHighlightRange, setNewHighlightRange] = useState<{ start: number; end: number } | null>(null);
  // Track text being deleted (for deletion animation - shows red then collapses)
  const [deletionRange, setDeletionRange] = useState<{ start: number; end: number } | null>(null);

  // Resizable divider state
  const [editorWidthPercent, setEditorWidthPercent] = useState(60);
  const [isDragging, setIsDragging] = useState(false);

  // Refs
  const chatViewportRef = useRef<HTMLDivElement>(null);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewContentRef = useRef<HTMLDivElement>(null);
  const mdeInstanceRef = useRef<any>(null);
  const hasGreetedRef = useRef(!!restoredState);
  const editorHasFocusedRef = useRef(false);
  const animationStartedForPatchRef = useRef<number>(-1); // Track which patch animation started for

  // Store access
  const updateProjectOverview = useAtlasStore((state) => state.updateSparkProjectOverview);

  // Navigation for citation drill-down
  const citationNavigate = useNavigate();
  const citationLocation = useLocation();
  // Refs for values only needed at call-time (avoids re-creating onNavigateToArticle on every keystroke/message)
  const workingBriefingRef = useRef(workingBriefing);
  useEffect(() => { workingBriefingRef.current = workingBriefing; }, [workingBriefing]);
  const messagesRef = useRef(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  const sessionIdRef = useRef(sessionId);
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  const onNavigateToArticle = useCallback((fqn: string, initialChapter?: string) => {
    // Save transient state to sessionStorage before navigating away
    const sessionKey = `briefing_assistant_${project.fqn}`;
    const stateToSave = {
      workingBriefing: workingBriefingRef.current,
      messages: messagesRef.current.map(m => ({ ...m, isAnimated: true, thinkingSteps: undefined })),
      sessionId: sessionIdRef.current,
    };
    sessionStorage.setItem(sessionKey, JSON.stringify(stateToSave));

    citationNavigate(`/viewer/${encodeURIComponent(fqn)}`, {
      state: {
        initialChapter,
        returnTo: citationLocation.pathname + citationLocation.search,
        returnState: { initialStepIndex: 1, restoreBriefingAssistant: true },
      }
    });
  }, [citationNavigate, citationLocation, project.fqn]);

  // Citation support for markdown rendering
  // Citations are resolved dynamically via bulk API for unknown refs
  const {
    markdownComponents,
    markdownPlugins,
    CitationPortal,
  } = useCitation({ projectFqn: project.fqn, onNavigateToArticle });

  // SSE Hook - errors are handled inline in the chat, no toast needed
  const handleSSEError = useCallback((_message: string) => {
    // Errors are displayed inline in the chat via error messages
    // No toast needed here
  }, []);

  const {
    sendMessage: sendSSEMessage,
    cancelRequest: cancelSSERequest,
    isLoading: isSSELoading,
    thinkingSteps,
    clearThinkingSteps,
  } = useBriefingSSE({
    projectFqn: project.fqn,
    onSessionCreated: setSessionId,
    onError: handleSSEError,
  });

  // === Helpers ===

  // Clear any saved session state from sessionStorage (called on explicit lifecycle events)
  const clearSessionState = useCallback(() => {
    sessionStorage.removeItem(`briefing_assistant_${project.fqn}`);
  }, [project.fqn]);

  // === Derived State ===

  const hasChanges = useMemo(() => {
    return normalize(workingBriefing) !== normalize(savedBriefing);
  }, [workingBriefing, savedBriefing]);

  const canEdit = !isChatting && !isAnimatingPatches;
  const canChat = !isEditing && !isAnimatingPatches;

  // Notify parent of mode state changes for alert bar
  useEffect(() => {
    onModeChange?.({ isEditing, isChatting, isStarting: isLoadingGreeting, isThinking: isSSELoading && !isLoadingGreeting });
  }, [isEditing, isChatting, isLoadingGreeting, isSSELoading, onModeChange]);

  // === Effects ===

  // Auto-greeting on mount
  // Uses setTimeout to defer past React StrictMode's sync unmount/remount cycle
  useEffect(() => {
    if (hasGreetedRef.current) return;

    const timer = setTimeout(() => {
      if (hasGreetedRef.current) return; // Double-check after timeout
      hasGreetedRef.current = true;
      (async () => {
        await sendGreeting();
        setIsLoadingGreeting(false);
      })();
    }, 50); // Small delay skips StrictMode's sync unmount/remount

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelSSERequest();
    };
  }, [cancelSSERequest]);

  // Navigation guard - beforeunload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes to the briefing. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  // Store handlers in refs for ESC handler
  const handleCancelEditRef = useRef<() => void>(() => { });
  const handleDiscardChangesRef = useRef<() => void>(() => { });
  const onCloseRef = useRef<() => void>(onClose);

  // Update refs when handlers change
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // ESC key handler - cancel edits when in edit mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isEditing) {
          e.preventDefault();
          handleCancelEditRef.current();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditing]);

  // Smooth scroll to bottom of chat - throttled to avoid performance issues
  const lastScrollTimeRef = useRef(0);
  const scrollToBottom = useCallback((smooth = true) => {
    if (!chatViewportRef.current) return;

    // Throttle scrolls to max once per 50ms for smooth experience
    const now = Date.now();
    if (now - lastScrollTimeRef.current < 50) return;
    lastScrollTimeRef.current = now;

    const viewport = chatViewportRef.current;
    if (smooth) {
      viewport.scrollTo({
        top: viewport.scrollHeight,
        behavior: 'smooth'
      });
    } else {
      viewport.scrollTop = viewport.scrollHeight;
    }
  }, []);

  // Auto-scroll chat to bottom on new messages
  useEffect(() => {
    scrollToBottom(false); // Immediate scroll for new messages
  }, [messages, scrollToBottom]);

  // Auto-scroll as thinking steps appear
  useEffect(() => {
    if (thinkingSteps.length > 0) {
      scrollToBottom(true);
    }
  }, [thinkingSteps, scrollToBottom]);

  // Stable callback for typewriter scroll (used by TypewriterContent)
  // Uses requestAnimationFrame to ensure DOM is painted before measuring scrollHeight,
  // and instant scroll to avoid smooth animation lag with fast-updating content
  const handleTypewriterProgress = useCallback(() => {
    requestAnimationFrame(() => {
      scrollToBottom(false);
    });
  }, [scrollToBottom]);

  // === Handlers ===

  const toggleThinkingPanel = useCallback((messageId: string) => {
    setExpandedThinkingPanels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  }, []);

  // Mark a message as having completed its typewriter animation
  const markMessageAnimated = useCallback((messageId: string) => {
    setMessages(prev => prev.map(msg =>
      msg.id === messageId ? { ...msg, isAnimated: true } : msg
    ));
  }, []);

  const sendGreeting = async () => {
    setIsChatting(true);

    try {
      const response = await sendSSEMessage(
        '<the user has entered the conversation>',
        workingBriefing,
        false,
        sessionId
      );

      if (response) {
        const aiMessage: BriefingMessage = {
          id: generateMessageId(),
          role: 'assistant',
          content: response.response,
          timestamp: new Date().toISOString(),
          patches: response.patches,
          thinkingSteps: response.thinkingSteps, // Use steps from response to avoid stale closure
        };

        setMessages([aiMessage]);

        // Preserve thinking panel expanded state when transitioning from live to message panel
        if (isLiveThinkingExpandedRef.current && response.thinkingSteps && response.thinkingSteps.length > 0) {
          setExpandedThinkingPanels(prev => new Set([...Array.from(prev), aiMessage.id]));
        }

        clearThinkingSteps();

        // Apply any patches from greeting
        if (response.patches && response.patches.length > 0) {
          applyPatches(response.patches, response.final_briefing);
        }

      } else {
        // Request was cancelled or failed, use fallback
        throw new Error('No response received');
      }
    } catch (error) {
      console.error('Failed to get greeting:', error);
      // Add a fallback greeting
      const fallbackGreeting: BriefingMessage = {
        id: generateMessageId(),
        role: 'assistant',
        content: `Hello! I'm here to help you refine your project briefing for "${project.name}". I can help you:\n\n- Clarify project goals and objectives\n- Structure the briefing for maximum clarity\n- Add missing strategic context\n- Improve specific sections\n\nHow can I assist you today?`,
        timestamp: new Date().toISOString(),
      };
      setMessages([fallbackGreeting]);
    } finally {
      setIsChatting(false);
    }
  };

  const handleSendMessage = async () => {
    const content = inputText.trim();
    if (!content || !canChat || isChatting) return;

    // Add user message optimistically
    const userMessage: BriefingMessage = {
      id: generateMessageId(),
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsChatting(true);

    try {
      const response = await sendSSEMessage(
        content,
        workingBriefing,
        briefingEditedSinceLastTurn,
        sessionId
      );

      if (response) {
        const aiMessage: BriefingMessage = {
          id: generateMessageId(),
          role: 'assistant',
          content: response.response,
          timestamp: new Date().toISOString(),
          patches: response.patches,
          thinkingSteps: response.thinkingSteps, // Use steps from response to avoid stale closure
        };

        setMessages(prev => [...prev, aiMessage]);

        // Preserve thinking panel expanded state when transitioning from live to message panel
        if (isLiveThinkingExpandedRef.current && response.thinkingSteps && response.thinkingSteps.length > 0) {
          setExpandedThinkingPanels(prev => new Set([...Array.from(prev), aiMessage.id]));
        }

        clearThinkingSteps();

        // Apply any patches
        if (response.patches && response.patches.length > 0) {
          applyPatches(response.patches, response.final_briefing);
        }

        // Reset the edit hint
        setBriefingEditedSinceLastTurn(false);
      } else {
        // Request failed or timed out - show inline error
        const errorMessage: BriefingMessage = {
          id: generateMessageId(),
          role: 'error',
          content: 'Something went wrong. Please try again.',
          timestamp: new Date().toISOString(),
          isAnimated: true, // No animation needed for errors
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error: any) {
      console.error('Failed to send message:', error);
      // Show inline error instead of toast
      const errorMessage: BriefingMessage = {
        id: generateMessageId(),
        role: 'error',
        content: error.message || 'Something went wrong. Please try again.',
        timestamp: new Date().toISOString(),
        isAnimated: true,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsChatting(false);
    }
  };

  // Strip markdown syntax to get plain text for matching
  const stripMarkdown = useCallback((text: string): string => {
    return text
      .replace(/#{1,6}\s*/g, '') // headers
      .replace(/\*\*|__/g, '') // bold
      .replace(/\*|_/g, '') // italic
      .replace(/`/g, '') // code
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
      .replace(/^\s*[-*+]\s*/gm, '') // list markers
      .replace(/^\s*\d+\.\s*/gm, '') // numbered lists
      .trim();
  }, []);

  const applyPatches = useCallback((patches: BriefingPatch[], finalBriefing: string) => {
    if (patches.length === 0) {
      // No patches, just update directly
      setWorkingBriefing(finalBriefing);
      return;
    }

    // Clear previous highlights when new patches arrive
    setHighlightedRanges([]);
    setDeletionRange(null);

    // Reset animation guard
    animationStartedForPatchRef.current = -1;

    setPendingPatches(patches);
    setCurrentPatchIndex(0);
    setIsAnimatingPatches(true);
    setPatchAnimationPhase('blur');
  }, []);

  // Scroll to the first highlighted or deletion element
  const scrollToHighlightedElement = useCallback(() => {
    if (!viewContentRef.current) return;

    // Find the first highlighted or deletion-pending element
    const highlightedEl = viewContentRef.current.querySelector(
      `.${styles.highlightedText}, .${styles.deletionText}`
    );

    if (highlightedEl) {
      highlightedEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  // Start the patch animation - for deletions, mark the text; for edits, just wait
  const startPatchAnimation = useCallback(() => {
    if (currentPatchIndex < 0 || currentPatchIndex >= pendingPatches.length) return;

    // Guard against multiple calls for the same patch
    if (animationStartedForPatchRef.current === currentPatchIndex) return;
    animationStartedForPatchRef.current = currentPatchIndex;

    const patch = pendingPatches[currentPatchIndex];
    const isDeletion = !patch.replace.trim();

    // Check if this is a "deletion with context" - where replace text is just
    // preserved context from the end of search (not actually new content)
    const isContextPreservingDeletion = !isDeletion &&
      patch.search.endsWith(patch.replace) &&
      patch.search.length > patch.replace.length;

    if (isDeletion || isContextPreservingDeletion) {
      // For deletions, mark the text to be deleted (will show in red)
      const searchPos = workingBriefing.indexOf(patch.search);
      if (searchPos !== -1) {
        // Calculate what's actually being deleted (excluding preserved context)
        const deletedLength = isDeletion
          ? patch.search.length
          : patch.search.length - patch.replace.length;

        if (deletedLength > 0) {
          setDeletionRange({
            start: searchPos,
            end: searchPos + deletedLength
          });
          // Scroll to deletion after a brief delay for DOM update
          setTimeout(scrollToHighlightedElement, 50);
        }
      }
      // Clear any previous green highlights
      setHighlightedRanges([]);
    }
  }, [currentPatchIndex, pendingPatches, workingBriefing, scrollToHighlightedElement]);

  // Apply a single patch and move to appropriate animation phase
  const applyCurrentPatch = useCallback(() => {
    if (currentPatchIndex < 0 || currentPatchIndex >= pendingPatches.length) return;

    const patch = pendingPatches[currentPatchIndex];
    const isDeletion = !patch.replace.trim();

    // Check if this is a "deletion with context" - where replace text is just
    // preserved context from the end of search (not actually new content)
    // e.g., search="## Delete This\n...\n\n## Keep This", replace="## Keep This"
    const isContextPreservingDeletion = !isDeletion &&
      patch.search.endsWith(patch.replace) &&
      patch.search.length > patch.replace.length;

    if (isDeletion || isContextPreservingDeletion) {
      // For deletions (including context-preserving ones), mark the deleted portion
      const searchPos = workingBriefing.indexOf(patch.search);
      if (searchPos !== -1) {
        // Calculate what's actually being deleted (excluding preserved context)
        const deletedLength = patch.search.length - patch.replace.length;
        if (deletedLength > 0) {
          setDeletionRange({
            start: searchPos,
            end: searchPos + deletedLength
          });
          setTimeout(scrollToHighlightedElement, 50);
        }
      }
      // Clear any previous green highlights for this type of change
      setHighlightedRanges([]);
      setPatchAnimationPhase('collapse');
    } else {
      // For additions/edits, apply the patch and set highlight
      setWorkingBriefing(prev => {
        const searchPos = prev.indexOf(patch.search);

        if (searchPos === -1) {
          // Patch search text not found - log warning and skip
          console.warn(`Patch search text not found: "${patch.search.slice(0, 50)}..."`);
          return prev;
        }

        // Calculate what portion is actually NEW (not preserved from search)
        let highlightStart = searchPos;
        let highlightEnd = searchPos + patch.replace.length;

        // Check if replace starts with content from search (prefix preservation)
        // This handles cases like: search="old prefix\nnew", replace="new prefix\nnew"
        // We only want to highlight the changed prefix, not the preserved suffix

        // For now, highlight the full replacement but skip if it's identical
        const searchStripped = stripMarkdown(patch.search);
        const replaceStripped = stripMarkdown(patch.replace);

        if (searchStripped !== replaceStripped) {
          // Set as new highlight for animation (will be moved to static after reveal)
          setNewHighlightRange({
            start: highlightStart,
            end: highlightEnd
          });
        }

        return prev.replace(patch.search, patch.replace);
      });

      // Scroll to the newly highlighted content after DOM update
      setTimeout(scrollToHighlightedElement, 50);

      setPatchAnimationPhase('reveal');
    }
  }, [currentPatchIndex, pendingPatches, stripMarkdown, scrollToHighlightedElement, workingBriefing]);

  // Move to next patch or finish animation
  const advanceToNextPatch = useCallback(() => {
    // Move current animation highlight to static list (no more animation)
    if (newHighlightRange) {
      setHighlightedRanges(prev => [...prev, newHighlightRange]);
      setNewHighlightRange(null);
    }

    const nextIndex = currentPatchIndex + 1;
    if (nextIndex < pendingPatches.length) {
      setCurrentPatchIndex(nextIndex);
      setPatchAnimationPhase('blur');
    } else {
      // All patches applied
      setIsAnimatingPatches(false);
      setPatchAnimationPhase('idle');
      setCurrentPatchIndex(-1);
      setPendingPatches([]);
      setBriefingEditedSinceLastTurn(false);
    }
  }, [currentPatchIndex, pendingPatches.length, newHighlightRange]);

  // Complete the deletion after collapse animation
  const completeDeletion = useCallback(() => {
    if (currentPatchIndex < 0 || currentPatchIndex >= pendingPatches.length) return;

    const patch = pendingPatches[currentPatchIndex];

    // Apply the actual deletion
    setWorkingBriefing(prev => prev.replace(patch.search, patch.replace));

    // Clear deletion range
    setDeletionRange(null);

    // Move to next patch
    advanceToNextPatch();
  }, [currentPatchIndex, pendingPatches, advanceToNextPatch]);

  // Animation timing effects
  useEffect(() => {
    if (!isAnimatingPatches) return;

    if (patchAnimationPhase === 'blur') {
      // At start of blur phase, mark deletions if applicable
      startPatchAnimation();

      // After blur phase, apply the patch and transition to next phase
      const timer = setTimeout(() => {
        applyCurrentPatch();
      }, 100); // Minimal delay to allow deletion marking before patch application
      return () => clearTimeout(timer);
    } else if (patchAnimationPhase === 'reveal') {
      // After reveal phase, move to next patch
      const timer = setTimeout(() => {
        advanceToNextPatch();
      }, 800); // Duration of reveal phase
      return () => clearTimeout(timer);
    } else if (patchAnimationPhase === 'collapse') {
      // After collapse animation, complete the deletion and move on
      const timer = setTimeout(() => {
        completeDeletion();
      }, 500); // Duration of collapse animation
      return () => clearTimeout(timer);
    }
  }, [isAnimatingPatches, patchAnimationPhase, applyCurrentPatch, advanceToNextPatch, startPatchAnimation, completeDeletion]);

  // Clear highlights when user manually edits
  const clearHighlights = useCallback(() => {
    setHighlightedRanges([]);
    setDeletionRange(null);
  }, []);

  const handleEnterEdit = () => {
    if (!canEdit) return;
    editorHasFocusedRef.current = false; // Reset focus tracking
    setEditSnapshot(workingBriefing);
    setIsEditing(true);
    // Clear green highlights when user starts editing
    clearHighlights();
  };

  const handleSaveEdit = useCallback(() => {
    // Only save on blur if the editor has actually been focused
    if (!editorHasFocusedRef.current) return;

    // Save on blur - get value from MDE instance
    const currentValue = mdeInstanceRef.current?.value?.() ?? workingBriefing;
    setWorkingBriefing(currentValue);
    setIsEditing(false);
    setBriefingEditedSinceLastTurn(true);
  }, [workingBriefing]);

  // Ref to always have latest save handler for CodeMirror blur event
  const handleSaveEditRef = useRef(handleSaveEdit);
  useEffect(() => {
    handleSaveEditRef.current = handleSaveEdit;
  }, [handleSaveEdit]);

  const handleCancelEdit = useCallback(() => {
    editorHasFocusedRef.current = false; // Prevent blur from saving
    setWorkingBriefing(editSnapshot);
    setIsEditing(false);
  }, [editSnapshot]);

  // Update ref when handler changes
  useEffect(() => {
    handleCancelEditRef.current = handleCancelEdit;
  }, [handleCancelEdit]);

  const handleSaveAndDone = useCallback(async () => {
    if (isSaving) return;

    setIsSaving(true);

    try {
      // Pass session ID so backend can persist citations from ephemeral index
      await updateProjectOverview(project.fqn, {
        briefing: workingBriefing,
        briefing_session_id: sessionId,
      });

      if (onBriefingUpdated) {
        onBriefingUpdated();
      }

      clearSessionState();
      onClose();
    } catch (error: any) {
      console.error('Failed to save briefing:', error);
      toast.error(`Failed to save: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, updateProjectOverview, project.fqn, workingBriefing, sessionId, onBriefingUpdated, onClose, clearSessionState]);

  const handleDiscardChanges = useCallback(() => {
    if (!hasChanges) {
      clearSessionState();
      onClose();
      return;
    }

    // Show confirmation modal
    setIsDiscardModalOpen(true);
  }, [hasChanges, onClose, clearSessionState]);

  const handleConfirmDiscard = useCallback(() => {
    setWorkingBriefing(savedBriefing);
    setIsDiscardModalOpen(false);
    clearSessionState();
    onClose();
  }, [savedBriefing, onClose, clearSessionState]);

  // Update ref when handler changes
  useEffect(() => {
    handleDiscardChangesRef.current = handleDiscardChanges;
  }, [handleDiscardChanges]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    saveAndClose: handleSaveAndDone,
    discardChanges: handleDiscardChanges,
    hasChanges,
    isSaving,
  }), [handleSaveAndDone, handleDiscardChanges, hasChanges, isSaving]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // === Resizable Divider Handlers ===

  const handleDividerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidthPercent = ((e.clientX - containerRect.left) / containerRect.width) * 100;

      // Clamp between 30% and 70%
      const clampedWidth = Math.min(70, Math.max(30, newWidthPercent));
      setEditorWidthPercent(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // === Memoized Values ===

  // Fix #5: Memoize SimpleMDE options to prevent re-initialization
  const simpleMdeOptions = useMemo(() => ({
    autofocus: false, // We handle focus manually in getMdeInstance
    spellChecker: false,
    status: false,
    toolbar: [
      'bold', 'italic', 'heading', '|',
      'unordered-list', 'ordered-list', '|',
      'link', 'quote', '|',
      'preview', 'guide'
    ] as const,
  }), []);

  // === Render Helpers ===

  const renderMessage = (message: BriefingMessage) => {
    const isUser = message.role === 'user';
    const isError = message.role === 'error';
    const isAssistant = message.role === 'assistant';
    const hasThinkingSteps = isAssistant && message.thinkingSteps && message.thinkingSteps.length > 0;

    // Render error messages with special styling
    if (isError) {
      return (
        <div key={message.id} className={styles.errorMessage}>
          <span className={styles.errorContent}>{message.content}</span>
        </div>
      );
    }

    return (
      <div
        key={message.id}
        className={`${styles.message} ${isUser ? styles.userMessage : styles.assistantMessage}`}
      >
        {isAssistant && (
          <div className={styles.messageHeader}>
            <AutoAwesomeIcon className={styles.assistantIcon} />
            <span className={styles.assistantLabel}>DeepSights</span>
          </div>
        )}
        {/* Thinking panel for AI messages - shown before content */}
        {hasThinkingSteps && (
          <div className={styles.messageThinkingPanel}>
            <ThinkingPanel
              steps={message.thinkingSteps!}
              isExpanded={expandedThinkingPanels.has(message.id)}
              onToggle={() => toggleThinkingPanel(message.id)}
            />
          </div>
        )}
        <div className={styles.messageContent}>
          {isUser ? (
            <ReactMarkdown>{normalizeMarkdownBullets(message.content)}</ReactMarkdown>
          ) : (
            <TypewriterContent
              content={message.content}
              isAnimated={!!message.isAnimated}
              onAnimationComplete={() => markMessageAnimated(message.id)}
              onTyping={handleTypewriterProgress}
              markdownComponents={markdownComponents}
              markdownPlugins={markdownPlugins}
            />
          )}
        </div>
        {message.patches && message.patches.length > 0 && (
          <div className={styles.patchIndicator}>
            Applied {message.patches.length} change{message.patches.length > 1 ? 's' : ''} to briefing
          </div>
        )}
      </div>
    );
  };

  /**
   * Create a rehype plugin that marks text nodes based on their source positions.
   *
   * Handles two types of markers:
   * - highlighted-patch: Green highlight for new/modified content
   * - deletion-pending: Red highlight for content about to be deleted
   *
   * This approach uses source position tracking instead of text matching,
   * which correctly marks only the actual patched text.
   */
  const createHighlightPlugin = useCallback(() => {
    return () => {
      return (tree: any) => {
        const hasStaticHighlights = highlightedRanges.length > 0;
        const hasNewHighlight = newHighlightRange !== null;
        const hasDeletion = deletionRange !== null;

        if (!hasStaticHighlights && !hasNewHighlight && !hasDeletion) return tree;

        // Walk the tree and find text nodes that overlap with marked ranges
        const visit = (node: any) => {
          if (node.type === 'text' && node.position) {
            const nodeStart = node.position.start.offset;
            const nodeEnd = node.position.end.offset;

            // Check for deletion range first (takes priority)
            if (hasDeletion && deletionRange) {
              if (nodeStart < deletionRange.end && nodeEnd > deletionRange.start) {
                const overlapStart = Math.max(0, deletionRange.start - nodeStart);
                const overlapEnd = Math.min(node.value.length, deletionRange.end - nodeStart);

                if (overlapStart < overlapEnd && overlapStart >= 0) {
                  const before = node.value.slice(0, overlapStart);
                  const marked = node.value.slice(overlapStart, overlapEnd);
                  const after = node.value.slice(overlapEnd);

                  const children: any[] = [];
                  if (before) children.push({ type: 'text', value: before });
                  if (marked) {
                    children.push({
                      type: 'element',
                      tagName: 'span',
                      properties: { className: ['deletion-pending'] },
                      children: [{ type: 'text', value: marked }]
                    });
                  }
                  if (after) children.push({ type: 'text', value: after });

                  if (children.length > 0) {
                    node.type = 'element';
                    node.tagName = 'span';
                    node.properties = {};
                    node.children = children;
                    delete node.value;
                  }
                  return; // Don't process highlights for this node
                }
              }
            }

            // Check for NEW highlight (animating) - takes priority
            if (hasNewHighlight && newHighlightRange) {
              if (nodeStart < newHighlightRange.end && nodeEnd > newHighlightRange.start) {
                const overlapStart = Math.max(0, newHighlightRange.start - nodeStart);
                const overlapEnd = Math.min(node.value.length, newHighlightRange.end - nodeStart);

                if (overlapStart < overlapEnd && overlapStart >= 0) {
                  const before = node.value.slice(0, overlapStart);
                  const highlighted = node.value.slice(overlapStart, overlapEnd);
                  const after = node.value.slice(overlapEnd);

                  const children: any[] = [];
                  if (before) children.push({ type: 'text', value: before });
                  if (highlighted) {
                    children.push({
                      type: 'element',
                      tagName: 'span',
                      properties: { className: ['highlighted-patch-new'] },
                      children: [{ type: 'text', value: highlighted }]
                    });
                  }
                  if (after) children.push({ type: 'text', value: after });

                  if (children.length > 0) {
                    node.type = 'element';
                    node.tagName = 'span';
                    node.properties = {};
                    node.children = children;
                    delete node.value;
                  }
                  return; // Don't process static highlights for this node
                }
              }
            }

            // Check for STATIC highlight ranges (already animated, no animation)
            if (hasStaticHighlights) {
              for (const range of highlightedRanges) {
                if (nodeStart < range.end && nodeEnd > range.start) {
                  const overlapStart = Math.max(0, range.start - nodeStart);
                  const overlapEnd = Math.min(node.value.length, range.end - nodeStart);

                  if (overlapStart < overlapEnd && overlapStart >= 0) {
                    const before = node.value.slice(0, overlapStart);
                    const highlighted = node.value.slice(overlapStart, overlapEnd);
                    const after = node.value.slice(overlapEnd);

                    const children: any[] = [];
                    if (before) children.push({ type: 'text', value: before });
                    if (highlighted) {
                      children.push({
                        type: 'element',
                        tagName: 'span',
                        properties: { className: ['highlighted-patch'] },
                        children: [{ type: 'text', value: highlighted }]
                      });
                    }
                    if (after) children.push({ type: 'text', value: after });

                    if (children.length > 0) {
                      node.type = 'element';
                      node.tagName = 'span';
                      node.properties = {};
                      node.children = children;
                      delete node.value;
                    }
                  }
                  break;
                }
              }
            }
          }

          // Recurse into children
          if (node.children) {
            for (const child of node.children) {
              visit(child);
            }
          }
        };

        visit(tree);
        return tree;
      };
    };
  }, [highlightedRanges, newHighlightRange, deletionRange]);

  // Memoize the rehype plugins array, including our custom highlight plugin
  const highlightingRehypePlugins = useMemo(() => {
    return [createHighlightPlugin()];
  }, [createHighlightPlugin]);

  // Custom markdown components that style the highlighted/deletion spans
  const highlightingMarkdownComponents = useMemo(() => {
    return {
      ...markdownComponents,
      // Style the marked spans from our rehype plugin
      span: ({ className, children, ...props }: any) => {
        const classNames = Array.isArray(className) ? className : (className ? [className] : []);
        // New highlight (currently animating) - uses animated class
        if (classNames.includes('highlighted-patch-new')) {
          return <span className={styles.highlightedTextNew} {...props}>{children}</span>;
        }
        // Static highlight (already animated) - no animation
        if (classNames.includes('highlighted-patch')) {
          return <span className={styles.highlightedText} {...props}>{children}</span>;
        }
        if (classNames.includes('deletion-pending')) {
          // Apply collapse animation class during collapse phase
          const deletionClass = patchAnimationPhase === 'collapse'
            ? `${styles.deletionText} ${styles.deletionCollapsing}`
            : styles.deletionText;
          return <span className={deletionClass} {...props}>{children}</span>;
        }
        return <span className={className} {...props}>{children}</span>;
      },
    };
  }, [markdownComponents, patchAnimationPhase]);

  const renderBriefingContent = () => {
    if (!workingBriefing) {
      return (
        <div className={styles.emptyBriefing}>
          Click here to start writing your project briefing...
        </div>
      );
    }

    // Render with citation support and position-based highlighting
    // The rehype plugin uses source positions to highlight only the exact patched text
    return (
      <div className={styles.briefingMarkdown}>
        <ReactMarkdown
          components={highlightingMarkdownComponents}
          remarkPlugins={markdownPlugins}
          rehypePlugins={highlightingRehypePlugins}
        >
          {normalizeMarkdownBullets(workingBriefing)}
        </ReactMarkdown>
      </div>
    );
  };

  // === Main Render ===

  return (
    <>
      <div
        className={`${styles.embeddedContainer} ${isDragging ? styles.isDragging : ''}`}
        ref={containerRef}
      >
        {/* Editor Panel - Left side */}
        <div
          className={styles.editorPanel}
          ref={editorContainerRef}
          style={{ width: `${editorWidthPercent}%` }}
        >
          {/* Upload info banner */}
          {showUploadBanner && (
            <div className={styles.uploadBanner}>
              <InfoOutlinedIcon />
              <span className={styles.uploadBannerText}>
                Document uploaded successfully. Review the content below and save to the project.
              </span>
              <button
                className={styles.uploadBannerDismiss}
                onClick={() => setShowUploadBanner(false)}
                title="Dismiss"
              >
                <CloseIcon />
              </button>
            </div>
          )}
          <div className={`${styles.editorContent} ${isChatting && !isLoadingGreeting ? styles.editorLocked : ''}`}>
            {/* View mode - click to edit */}
            <div
              ref={viewContentRef}
              className={`${styles.viewContent} ${canEdit ? styles.viewContentEditable : ''} ${patchAnimationPhase === 'reveal' ? styles.viewContentRevealing : ''}`}
              onMouseDown={canEdit ? (e) => { e.preventDefault(); handleEnterEdit(); } : undefined}
              role={canEdit ? 'button' : undefined}
              tabIndex={canEdit ? 0 : undefined}
              onKeyDown={canEdit ? (e) => { if (e.key === 'Enter') handleEnterEdit(); } : undefined}
              style={{ display: isEditing ? 'none' : 'block' }}
            >
              {renderBriefingContent()}
            </div>

            {/* Edit mode - SimpleMDE editor */}
            {isEditing && (
              <SimpleMDE
                value={workingBriefing}
                onChange={setWorkingBriefing}
                getMdeInstance={(inst: any) => {
                  mdeInstanceRef.current = inst;
                  const cm = inst?.codemirror;
                  if (cm) {
                    // Refresh and focus after mount
                    requestAnimationFrame(() => {
                      cm.refresh?.();
                      cm.focus?.();
                    });
                    // Delay attaching blur handler to avoid immediate trigger
                    setTimeout(() => {
                      editorHasFocusedRef.current = true;
                      cm.on('blur', () => handleSaveEditRef.current());
                    }, 150);
                  }
                }}
                options={simpleMdeOptions}
              />
            )}
          </div>
        </div>

        {/* Resizable Divider */}
        <div
          className={styles.resizeDivider}
          onMouseDown={handleDividerMouseDown}
        >
          <div className={styles.resizeDividerHandle} />
        </div>

        {/* Chat Panel - Right side */}
        <div
          className={styles.chatPanel}
          style={{ width: `${100 - editorWidthPercent}%` }}
        >
          <div className={styles.chatViewport} ref={chatViewportRef}>
            {isLoadingGreeting && messages.length === 0 && (
              <div className={styles.greetingLoader}>
                <CircularProgress size={24} />
                <span>Starting assistant...</span>
              </div>
            )}
            {messages.map(renderMessage)}
            {/* Live thinking panel during processing (before attached to message) */}
            {isSSELoading && thinkingSteps.length > 0 && !isLoadingGreeting && (
              <div className={styles.liveThinkingPanel}>
                <ThinkingPanel
                  steps={thinkingSteps}
                  isExpanded={isLiveThinkingExpanded}
                  onToggle={() => {
                    setIsLiveThinkingExpanded(prev => {
                      const newValue = !prev;
                      isLiveThinkingExpandedRef.current = newValue;
                      return newValue;
                    });
                  }}
                />
              </div>
            )}
            {/* Typing indicator: show when waiting for response */}
            {isChatting && (
              thinkingSteps.length === 0 ||
              thinkingSteps.every(s => s.status === 'completed')
            ) && !isLoadingGreeting && (
                <div className={styles.thinkingIndicator}>
                  <span></span><span></span><span></span>
                </div>
              )}
          </div>
          <div className={styles.chatInput}>
            <div className={styles.inputContainer}>
              <TextareaAutosize
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  !canChat
                    ? 'Finish editing to continue chatting...'
                    : 'Ask about or request changes to the briefing...'
                }
                disabled={!canChat || isChatting}
                className={styles.chatTextarea}
                minRows={1}
                maxRows={6}
              />
              <div className={styles.inputFooter}>
                <button
                  className={styles.sendButton}
                  onClick={handleSendMessage}
                  disabled={!canChat || isChatting || !inputText.trim()}
                  title="Send (Cmd/Ctrl + Enter)"
                >
                  <SendIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Citation Portal for lightboxes and tooltips */}
      <CitationPortal />

      {/* Discard confirmation modal */}
      <ConfirmationModal
        isOpen={isDiscardModalOpen}
        onClose={() => setIsDiscardModalOpen(false)}
        onConfirm={handleConfirmDiscard}
        title="Discard Changes?"
        message="Are you sure you want to discard all changes to the briefing? This cannot be undone."
        confirmText="Discard"
        cancelText="Cancel"
        variant="destructive"
      />
    </>
  );
});

export default BriefingAssistantOverlay;
